import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileCheck2, Check, X, Eye, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATUS_TONE = {
  pending: "bg-warning/15 text-warning",
  confirmed: "bg-success/15 text-success",
  rejected: "bg-destructive/15 text-destructive",
};

const STATUS_LABEL = { pending: "Menunggu", confirmed: "Dikonfirmasi", rejected: "Ditolak" };

export default function TransferProofs() {
  const [status, setStatus] = useState("pending");
  const qs = new URLSearchParams();
  if (status !== "all") qs.set("status", status);
  const { data: proofs = [], isLoading } = useSWR(`/proofs?${qs.toString()}`, fetcher);
  const { data: tenants = [] } = useSWR("/tenants", fetcher);
  const { data: units = [] } = useSWR("/units", fetcher);
  const { data: bills = [] } = useSWR("/bills", fetcher);
  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);
  const billMap = useMemo(() => Object.fromEntries(bills.map((b) => [b.id, b])), [bills]);
  const unitMap = useMemo(() => Object.fromEntries(units.map((u) => [u.id, u])), [units]);
  const [reviewing, setReviewing] = useState(null); // {proof, action}

  const doReview = async (note) => {
    const { proof, action } = reviewing;
    try {
      await api.post(`/proofs/${proof.id}/review`, { action, review_note: note });
      toast.success(action === "confirm" ? "Bukti dikonfirmasi & pembayaran dicatat" : "Bukti ditolak");
      setReviewing(null);
      mutate((k) => typeof k === "string" && k.startsWith("/proofs"));
      mutate((k) => typeof k === "string" && k.startsWith("/bills"));
      mutate("/dashboard/stats");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="proofs-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Konfirmasi Pembayaran</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Bukti Transfer</h1>
          <p className="text-sm text-muted-foreground mt-1">Tinjau bukti transfer yang dikirim penyewa via portal.</p>
        </div>
        <div className="w-full sm:w-56">
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="proof-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="pending">Menunggu</SelectItem>
              <SelectItem value="confirmed">Dikonfirmasi</SelectItem>
              <SelectItem value="rejected">Ditolak</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : proofs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <ClipboardList className="h-7 w-7" />
            </div>
            <div className="font-display text-lg font-medium">Tidak ada bukti transfer</div>
            <p className="text-sm text-muted-foreground -mt-2">Bukti akan muncul di sini saat penyewa mengirim via portal.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {proofs.map((p) => {
            const tenant = tenantMap[p.tenant_id];
            const bill = p.bill_id ? billMap[p.bill_id] : null;
            const unit = bill ? unitMap[bill.unit_id] : null;
            return (
              <Card key={p.id} data-testid={`proof-row-${p.id}`}>
                <CardContent className="p-4 sm:p-5 flex flex-col md:flex-row gap-4">
                  <a
                    href={`${API}/proofs/${p.id}/file`}
                    target="_blank" rel="noreferrer"
                    className="h-24 w-24 rounded-md overflow-hidden border border-border/60 bg-muted shrink-0 relative"
                    data-testid={`proof-file-${p.id}`}
                  >
                    <FileCheck2 className="h-6 w-6 text-muted-foreground absolute inset-0 m-auto" />
                    <img
                      src={`${API}/proofs/${p.id}/file`}
                      alt="Bukti"
                      className="h-full w-full object-cover relative z-[1]"
                      onError={(e) => { e.currentTarget.remove(); }}
                    />
                  </a>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${STATUS_TONE[p.status]}`}>
                        {STATUS_LABEL[p.status]}
                      </Badge>
                      {unit && <Badge className="rounded-md font-mono">{unit.code}</Badge>}
                      <span className="text-xs text-muted-foreground">{formatDate(p.paid_at)}</span>
                    </div>
                    <div className="mt-2 font-medium">{tenant?.name || "—"}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {p.method === "cash" ? "Cash" : "Transfer"} · {formatIDR(p.amount)}
                      {bill && ` · Tagihan ${formatDate(bill.period_start)}–${formatDate(bill.period_end)}`}
                    </div>
                    {p.note && <div className="text-sm mt-2">{p.note}</div>}
                    {p.review_note && p.status !== "pending" && (
                      <div className="text-xs text-muted-foreground mt-1 italic">
                        Catatan review: {p.review_note}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0 md:flex-col md:justify-center">
                    {p.status === "pending" ? (
                      <>
                        <Button size="sm" onClick={() => setReviewing({ proof: p, action: "confirm" })} className="gap-1" data-testid={`confirm-proof-${p.id}`}>
                          <Check className="h-4 w-4" /> Konfirmasi
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReviewing({ proof: p, action: "reject" })} className="gap-1" data-testid={`reject-proof-${p.id}`}>
                          <X className="h-4 w-4" /> Tolak
                        </Button>
                      </>
                    ) : (
                      <a href={`${API}/proofs/${p.id}/file`} target="_blank" rel="noreferrer" className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" /> Lihat file
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {reviewing && (
        <ReviewDialog
          reviewing={reviewing}
          onClose={() => setReviewing(null)}
          onSubmit={doReview}
        />
      )}
    </div>
  );
}

function ReviewDialog({ reviewing, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const isConfirm = reviewing.action === "confirm";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isConfirm ? "Konfirmasi Bukti Transfer" : "Tolak Bukti Transfer"}</DialogTitle>
          <DialogDescription>
            {isConfirm
              ? "Pembayaran akan otomatis dicatat pada tagihan terkait dan kwitansi dibuat."
              : "Jelaskan alasan penolakan agar penyewa dapat mengirim ulang."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-border/60 p-3 text-sm">
            <div className="font-medium">{formatIDR(reviewing.proof.amount)}</div>
            <div className="text-xs text-muted-foreground">{reviewing.proof.method === "cash" ? "Cash" : "Transfer"} · {formatDate(reviewing.proof.paid_at)}</div>
          </div>
          <div className="space-y-1.5">
            <Label>Catatan (opsional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="review-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={() => onSubmit(note)} variant={isConfirm ? "default" : "destructive"} data-testid="review-submit-button">
            {isConfirm ? "Ya, Konfirmasi" : "Tolak"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
